import React, { useEffect, useState } from "react";
import { Sparkles, Beaker } from "lucide-react";
import api, { friendlyError } from "@/lib/api";
import { Card, Button, Chip, Field, Input, Select } from "@/ui";

const KIND_LABEL = {
  openrouter: "OpenRouter — many models, one key (recommended)",
  anthropic: "Anthropic — Claude, direct",
  openai_compatible: "OpenAI-compatible gateway — base URL + key",
};
const JOB_ORDER = ["chat", "vision", "voice", "weekly"];

// Platform Owner: the swappable AI. A key per provider, a model per job, a real
// Test call, and this month's spend. No provider is baked into the code.
export default function AiModelsPanel({ onToast }) {
  const [cfg, setCfg] = useState(null);
  const [keys, setKeys] = useState({});
  const [bases, setBases] = useState({});
  const [jobs, setJobs] = useState({});
  const [plan, setPlan] = useState("");
  const [busy, setBusy] = useState("");
  const [tests, setTests] = useState({});
  const [loadErr, setLoadErr] = useState("");

  const load = () => api.get("/platform/ai").then((r) => {
    setLoadErr("");
    setCfg(r.data); setJobs(r.data.jobs || {}); setPlan(r.data.byokPlan || "");
    const b = {}; Object.entries(r.data.providers || {}).forEach(([k, v]) => { b[k] = v.baseUrl || ""; });
    setBases(b);
  }).catch((e) => setLoadErr(friendlyError(e)));
  useEffect(() => { load(); }, []);

  const saveProvider = async (kind) => {
    setBusy("prov-" + kind);
    try {
      const body = { kind, baseUrl: bases[kind] || "" };
      if ((keys[kind] || "").trim()) body.apiKey = keys[kind].trim();
      const r = await api.post("/platform/ai/provider", body);
      setCfg(r.data); setKeys((k) => ({ ...k, [kind]: "" }));
      onToast?.("AI provider saved.");
    } catch (e) { onToast?.(friendlyError(e)); } finally { setBusy(""); }
  };

  const saveJobs = async () => {
    setBusy("jobs");
    try { const r = await api.post("/platform/ai/jobs", { jobs }); setCfg(r.data); setJobs(r.data.jobs); onToast?.("Model choices saved."); }
    catch (e) { onToast?.(friendlyError(e)); } finally { setBusy(""); }
  };

  const savePlan = async () => {
    setBusy("plan");
    try { const r = await api.post("/platform/ai/byok-plan", { plan }); setCfg(r.data); onToast?.("Plan saved."); }
    catch (e) { onToast?.(friendlyError(e)); } finally { setBusy(""); }
  };

  const runTest = async (job) => {
    setBusy("test-" + job);
    try { const r = await api.post("/platform/ai/test", { job }); setTests((t) => ({ ...t, [job]: r.data })); }
    catch (e) { setTests((t) => ({ ...t, [job]: { ok: false, message: friendlyError(e) } })); } finally { setBusy(""); }
  };

  if (loadErr) return (
    <Card data-testid="ai-models-error" style={{ marginBottom: 16 }}>
      <p className="t-small" style={{ marginBottom: 12 }}>{loadErr}</p>
      <Button data-testid="ai-models-retry" onClick={load}>Try again</Button>
    </Card>
  );
  if (!cfg) return <Card data-testid="ai-models-loading" style={{ marginBottom: 16 }}><div className="sk sk-title" style={{ marginBottom: 12 }} /><div className="sk sk-card" /></Card>;
  const kinds = cfg.kinds || [];
  const ms = cfg.monthSpend || {};

  return (
    <Card data-testid="ai-models-card" style={{ marginBottom: 16 }}>
      <div className="row-between" style={{ marginBottom: 8 }}>
        <div className="row" style={{ gap: 10 }}>
          <span className="ph-icon" style={{ margin: 0 }}><Sparkles size={18} strokeWidth={1.75} /></span>
          <h2 className="t-h2">AI models</h2>
        </div>
        <Chip tone="neutral" data-testid="ai-month-spend">this month: {ms.cost || "\u20b10.00"} · {ms.calls || 0} calls</Chip>
      </div>
      <p className="muted t-small" style={{ marginBottom: 14, maxWidth: 680 }}>
        Reto is never tied to one AI company. Add a fallback key per provider, then pick which model does each job.
        A shop key never shows again — only the last four.
      </p>

      {/* provider fallback keys */}
      <div className="stack" style={{ gap: 12 }}>
        {kinds.map((kind) => {
          const pv = (cfg.providers || {})[kind] || {};
          return (
            <div key={kind} className="tbl-wrap" data-testid={`ai-provider-${kind}`} style={{ padding: 12 }}>
              <div className="row-between" style={{ marginBottom: 8 }}>
                <span className="t-small" style={{ fontWeight: 600 }}>{KIND_LABEL[kind] || kind}</span>
                <Chip tone={pv.configured ? "good" : "warn"} dot>{pv.configured ? `key \u2026${(pv.keyMasked || "").replace("\u2026", "")}` : "no key"}</Chip>
              </div>
              <div className="grid grid-2" style={{ gap: 10, alignItems: "end" }}>
                <Field label="API key" htmlFor={`k-${kind}`}>
                  <Input id={`k-${kind}`} type="password" data-testid={`ai-key-${kind}`} value={keys[kind] || ""}
                    onChange={(e) => setKeys((k) => ({ ...k, [kind]: e.target.value }))}
                    placeholder={pv.configured ? "\u2022\u2022\u2022\u2022 (blank keeps it)" : "paste key"} />
                </Field>
                {kind !== "anthropic" ? (
                  <Field label="Base URL" htmlFor={`b-${kind}`} hint={kind === "openrouter" ? "leave blank for OpenRouter default" : "your gateway URL"}>
                    <Input id={`b-${kind}`} data-testid={`ai-base-${kind}`} value={bases[kind] || ""}
                      onChange={(e) => setBases((b) => ({ ...b, [kind]: e.target.value }))} placeholder="https://…/v1" />
                  </Field>
                ) : <div />}
              </div>
              <div style={{ marginTop: 10 }}>
                <Button size="sm" variant="primary" data-testid={`ai-save-${kind}`} loading={busy === "prov-" + kind} onClick={() => saveProvider(kind)}>Save {kind}</Button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="divider" style={{ margin: "16px 0" }} />

      {/* model per job */}
      <h3 className="t-h3" style={{ marginBottom: 10 }}>which model does each job</h3>
      <div className="stack" style={{ gap: 10 }}>
        {JOB_ORDER.map((job) => {
          const jc = jobs[job] || {}; const t = tests[job];
          return (
            <div key={job} data-testid={`ai-job-${job}`} className="grid" style={{ gridTemplateColumns: "1.2fr 1fr 1.4fr auto", gap: 8, alignItems: "end" }}>
              <div>
                <div className="t-small" style={{ fontWeight: 600 }}>{job}</div>
                <div className="muted t-small">{(cfg.jobLabels || {})[job]}</div>
              </div>
              <Field label="provider" htmlFor={`jp-${job}`}>
                <Select id={`jp-${job}`} data-testid={`ai-job-provider-${job}`} value={jc.provider || "openrouter"}
                  onChange={(e) => setJobs((j) => ({ ...j, [job]: { ...j[job], provider: e.target.value } }))}>
                  {kinds.map((k) => <option key={k} value={k}>{k}</option>)}
                </Select>
              </Field>
              <Field label="model" htmlFor={`jm-${job}`}>
                <Input id={`jm-${job}`} data-testid={`ai-job-model-${job}`} value={jc.model || ""}
                  onChange={(e) => setJobs((j) => ({ ...j, [job]: { ...j[job], model: e.target.value } }))} placeholder="model name" />
              </Field>
              <Button size="sm" data-testid={`ai-test-${job}`} loading={busy === "test-" + job} onClick={() => runTest(job)}><Beaker size={14} strokeWidth={1.75} />test</Button>
              {t ? <div className="t-small" data-testid={`ai-test-result-${job}`} style={{ gridColumn: "1 / -1", color: t.ok ? "var(--good)" : "var(--bad)" }}>{t.message}</div> : null}
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 12 }}>
        <Button variant="primary" data-testid="ai-save-jobs" loading={busy === "jobs"} onClick={saveJobs}>Save model choices</Button>
      </div>

      <div className="divider" style={{ margin: "16px 0" }} />
      <div className="row" style={{ gap: 10, alignItems: "end", flexWrap: "wrap" }}>
        <Field label="Which plan can bring their own key" htmlFor="byok-plan" hint="usage on their key bills to them">
          <Input id="byok-plan" data-testid="ai-byok-plan" value={plan} onChange={(e) => setPlan(e.target.value)} placeholder="pro" />
        </Field>
        <Button data-testid="ai-save-plan" loading={busy === "plan"} onClick={savePlan}>Save plan</Button>
      </div>
    </Card>
  );
}
